import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { pushEvent, isTest } from './clevertap';
import { useTrackingConfigContext } from './TrackingConfigProvider';

export type TrackingContext = {
    /**
     * Logs warning if no provider exist for the tracking context
     */
    trackEvent: (
        eventName: string,
        eventPayload?: Record<string, unknown>,
        eventType?: 'view' | 'close' | 'click',
    ) => void;
    trackEventV2?: (
        eventType: 'view' | 'close' | 'click' | 'success' | 'fail' | 'submit',
        eventPayload?: {
            name?: string;
            type?:
                | 'Card'
                | 'Button'
                | 'Link'
                | 'Page'
                | 'Modal'
                | 'Tab'
                | 'Banner'
                | 'Text'
                | 'Slider'
                | 'Input'
                | 'Checkbox'
                | 'Other'
                | string;
        } & Record<string, unknown>,
    ) => void | undefined;
    trackEventRaw: (
        event: string,
        eventPayload?: Record<string, unknown>,
    ) => void;
    trackOpenCloseEvent: (open: boolean, location?: string) => void;
    pageName?: string;
    hideInChild?: boolean;
    getPageName?: () => string | undefined;
};

export type TrackingProviderProps = {
    pageName?: string;
    trackPageVisit: boolean;
    hideInChild?: boolean;
    viewType?: 'page' | 'tab' | 'modal' | 'banner' | 'other' | string;
    trackUnmountEvent?: boolean;
    trackingContextProps?: Partial<TrackingContext>;
    customParams?: Record<string, string>;
};

export const TrackingContext = createContext<TrackingContext>({
    trackEvent: () => {
        if (!isTest) console.warn('Tracking context is not initialized');
    },
    trackEventV2: () => {
        if (!isTest) console.warn('Tracking context is not initialized');
    },
    trackEventRaw: () => {
        if (!isTest) console.warn('Tracking context is not initialized');
    },
    pageName: '',
    trackOpenCloseEvent: () => {
        if (!isTest) console.warn('Tracking context is not initialized');
    },
});

export const useTrackingContext = () => useContext(TrackingContext);

/**
 * Provides page name for consumer components
 *
 * You can stack multiple TrackingProviders insed each other to combine page names
 *
 * By default it will send `Page_Visit` event when component is mounted
 * @param pageName - name of the page
 * @param trackPageVisit - send `Page_Visit` event
 * @example
 * <TrackingProvider pageName="parentPage">
 *     <TrackingProvider pageName="subPage">
 *         const { pageName, trackEvent } = useTrackingContext();
 *         // pageName is `parentPage_subPage` here
 *
 *         trackEvent('myEvent')
 *         // this will track 'parentPage_subPage_myEvent' event
 *     </TrackingProvider>
 * </TrackingProvider>
 */
export const TrackingProvider: React.FCC<TrackingProviderProps> = ({
    children,
    pageName,
    trackPageVisit,
    hideInChild,
    viewType = 'page',
    trackingContextProps = {},
    customParams = {},
}) => {
    const { getPayload } = useTrackingConfigContext();
    const parentCtx = useTrackingContext();
    const getNamePrefix = useCallback(
        (
            currentPageName: string | undefined,
            parentPageName: string | undefined,
            parentHideInChild: boolean | undefined,
            viewType: 'page' | 'tab' | 'modal' | 'banner' | 'other' | string,
        ) => {
            if (!currentPageName) return '';
            let pageIdentifier = '';
            if (parentPageName && !parentHideInChild) {
                switch (viewType) {
                    case 'page':
                        pageIdentifier = `${parentPageName} (${currentPageName})`;
                        break;
                    case 'tab':
                        pageIdentifier = `${parentPageName} (${currentPageName} Tab)`;
                        break;
                    case 'modal':
                        pageIdentifier = `${parentPageName} [${currentPageName} Modal]`;
                        break;
                    case 'banner':
                        pageIdentifier = `${parentPageName} [${currentPageName} Banner]`;
                        break;
                    case 'other':
                        pageIdentifier = `${parentPageName} [${currentPageName}]`;
                        break;
                    default:
                        pageIdentifier = `${parentPageName} (${currentPageName})`;
                        break;
                }
            } else {
                switch (viewType) {
                    case 'page':
                        pageIdentifier = `${currentPageName} Page`;
                        break;
                    case 'tab':
                        pageIdentifier = `${currentPageName} Tab`;
                        break;
                    case 'modal':
                        pageIdentifier = `${currentPageName} Modal`;
                        break;
                    case 'banner':
                        pageIdentifier = `${currentPageName} Banner`;
                        break;
                    case 'other':
                        pageIdentifier = `[${currentPageName}]`;
                        break;
                    default:
                        pageIdentifier = currentPageName;
                        break;
                }
            }
            return pageIdentifier;
        },
        [],
    );
    const eventLocation = useMemo(
        () =>
            trackingContextProps.getPageName
                ? trackingContextProps.getPageName()
                : getNamePrefix(
                      pageName,
                      parentCtx.pageName,
                      parentCtx.hideInChild,
                      viewType,
                  ),
        [
            getNamePrefix,
            pageName,
            parentCtx.hideInChild,
            parentCtx.pageName,
            trackingContextProps,
            viewType,
        ],
    );

    const trackEvent = useCallback(
        (
            eventName: string,
            payload?: Record<string, unknown>,
            eventType: 'view' | 'close' | 'click' = 'click',
        ) => {
            const extraPayload = getPayload();
            const eventIdentifier =
                eventType === 'view'
                    ? 'View'
                    : eventType === 'close'
                    ? 'Close'
                    : 'Click';
            const eventPrefix = eventLocation
                ? `${eventIdentifier} ${eventLocation}`
                : `${eventIdentifier}`;
            const event = eventName
                ? `${eventPrefix} ${eventName}`
                : eventPrefix;
            pushEvent(
                event,
                Object.assign({}, payload, extraPayload, customParams),
            );
        },
        [eventLocation, getPayload, customParams],
    );

    const trackEventRaw = useCallback(
        (event: string, payload?: Record<string, unknown>) => {
            const extraPayload = getPayload();
            pushEvent(event, Object.assign({}, payload, extraPayload));
        },
        [getPayload],
    );

    const trackOpenCloseEvent = useCallback(
        (open: boolean, location?: string) => {
            if (open) {
                trackEvent(location || '', undefined, 'view');
            } else {
                trackEvent(location || '', undefined, 'close');
            }
        },
        [trackEvent],
    );

    useMemo(() => {
        if (!trackPageVisit || !pageName?.trim()) return;
        trackOpenCloseEvent(true);
        //track page visit only on load
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <TrackingContext.Provider
            value={{
                trackEvent: trackingContextProps.trackEvent || trackEvent,
                trackEventRaw:
                    trackingContextProps.trackEventRaw || trackEventRaw,
                pageName: eventLocation,
                trackOpenCloseEvent:
                    trackingContextProps.trackOpenCloseEvent ||
                    trackOpenCloseEvent,
                hideInChild,
                trackEventV2: trackingContextProps.trackEventV2 || undefined,
            }}
        >
            {children}
        </TrackingContext.Provider>
    );
};

/* eslint @typescript-eslint/no-duplicate-enum-values: 0 */
export enum PAGE_NAME {
    Login = 'Login',
    DepositDownload = 'Deposit Download',
    ResetPassword = 'Reset Password',
    RecoverPassword = 'Recover Password',
    ResetPasswordDone = 'Reset Password Done',
    ChangePassword = 'Change Password',
    Root = 'Root',
    Overview = 'Overview',
    Confirm = 'Confirm',
    Invite = 'Invite',
    Resend = 'Resend',
    Donations = 'Donations',
    DonationActivity = 'DonationsActivityTab',
    DonationSummary = 'DonationSummaryTab',
    BankDeposits = 'Bank Deposits',
    Refunds = 'Refunds',
    Donors = 'Donors',
    DonorsNew = 'New Donors',
    DonorProfile = 'DonorProfile',
    DonorProfileHistoryTab = 'Donor Profile History tab',
    DonorProfileMessageTab = 'Donor Profile Message tab',
    InviteDonor = 'Invite Donor',
    Integrations = 'Integrations',
    IntegrationsHomeTab = 'Home Tab',
    IntegrationsDonorsTab = 'Donors Tab',
    IntegrationsEnvelopesTab = 'Envelopes Tab',
    IntegrationsExportedTab = 'Exported Donations Tab',
    IntegrationsSettingsTab = 'Settings Tab',
    IntegrationsCustomReports = 'Custom Reports',
    Navigation = 'Navigation',
    PrimaryRepresentativeNotification = 'Primary Representative Notification',
    PrimaryRepresentativeNonAdminNotification = 'Primary Representative Non Admin Notification',
    ReportGenerate = 'Generate Report Page',
    Reports = 'Reports',
    ReportsRoot = 'Root',
    ReportsGenerateTab = 'Generate Reports Tab',
    ReportsOldDashboardTab = 'Old Dashboard Reports Tab',
    ReportsHistory = 'Reports History Tab',
    ReportsReconciliation = 'Reconciliation',
    Events = 'Events',
    Settings = 'Settings',
    SettingsRoot = 'Root',
    SettingsAppProfile = 'App Profile',
    SettingsAppProfileCustomizeProfile = 'Customize Profile Tab',
    SettingsAppProfileCustomizeReceipt = 'Customize Receipt Tab',
    SettingsCampuses = 'Campuses',
    SettingsQuickGive = 'Quick Give',
    SettingsEnvelopes = 'Envelopes',
    SettingsEnvelopesActive = 'Active',
    SettingsEnvelopesInactive = 'Inactive',
    SettingsUsers = 'Users',
    SettingsBankInfo = 'Bank Info',
    SettingsBankInfoPrimaryRepTab = 'Primary representative Tab',
    SettingsBankInfoBankAccountTab = 'Next-day deposit Tab',
    SettingsOrganizationInfo = 'Organziation Info',
    SettingsOrganizationInfoPhysicalAddress = 'Physical Address',
    SettingsOrganizationInfoMailingAddress = 'Mailing Address',
    SettingsOrganizationInfoContactInfo = 'Contact Info',
    SettingsOrganizationInfoSocialInfo = 'Social Info',
    SettingsMissionStatement = 'Mission Statement',
    SettingsCoveredFees = 'DCF Settings',
    SocialMediaGiving = 'Social Media Giving',
    FacebookGiving = 'FacebookGiving',
    TwitterGiving = 'TwitterGiving',
    InstagramGiving = 'InstagramGiving',
    YouTubeGiving = 'YouTubeGiving',
    OnlineGiving = 'Online Giving',
    GivingTools = 'Giving Tools',
    Givelithon = 'Givelithon',
    GivelithonLaunch = 'GivelithonLaunch',
    GivelifyButtonPage = 'GivelifyButtonPage',
    GivelifyButtonPagePublic = 'GivelifyButtonPagePublic',
    EditProfile = 'Edit Profile',
    Datepicker = 'Datepicker',
    CaptivePortal = 'Captive Portal',
    SeedsOfGood = 'SoG',
}

export enum MODAL_NAME {
    DepositDetails = '[Deposit Details Modal]',
    DonationDetails = '[Donation Details Modal]',
    GivingStyleHelp = '[Giving Style Help Modal]',
    ConfirmCancelBox = '[Confirm Cancel Box]',
    ConfirmDeleteBox = '[Confirm Delete Box]',
    CanNotDeleteBox = '[Can Not Delete Box]',
    ConfirmShowBox = '[Confirm Show Box]',
    ConfirmHideBox = '[Confirm Hide Box]',
    CreateEnvelope = '[Create Envelope Modal]',
    EditEnvelope = '[Edit Envelope Modal]',
    AddPrimaryRepresentative = '[Add Primary Representative]',
    AddPrimaryRepresentativeNonAdmin = '[Add Primary Representative Non Admin]',
    SeedsOfGoodDonations = 'DonationInfo',
}
